import React, { useEffect, useRef, useState } from "react";
import { club } from "../mock";
import { loadYT } from "../lib/video";

// YouTube-backed background that cycles between the provided timestamp windows.
// The seek/re-buffer gap between windows is masked with a twirling, fading crest.
export default function HeroVideoLoop({
  videoId = "0yxWOdNx2M0",
  poster,
  loops = [],
  logoBetween = true,
}) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const segRef = useRef(0);
  const segments = Array.isArray(loops) && loops.length ? loops : null;
  const [masking, setMasking] = useState(true);

  useEffect(() => {
    let mounted = true;
    let interval;
    const firstStart = segments ? segments[0].start : 0;
    const host = hostRef.current;
    if (!host) return undefined;
    // Mount the YT player on a manually-created child node so the YouTube API can replace it
    // with its <iframe> without corrupting React's DOM tree (prevents the
    // "removeChild: node is not a child of this node" crash on unmount).
    const mountNode = document.createElement("div");
    host.appendChild(mountNode);

    loadYT().then((YT) => {
      if (!mounted) return;
      playerRef.current = new YT.Player(mountNode, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          playsinline: 1,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          disablekb: 1,
          iv_load_policy: 3,
          fs: 0,
          loop: segments ? 0 : 1,
          playlist: segments ? undefined : videoId,
          start: firstStart,
        },
        events: {
          onReady: (e) => {
            e.target.mute();
            if (segments) e.target.seekTo(firstStart, true);
            e.target.playVideo();
          },
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.PLAYING) {
              setTimeout(() => mounted && setMasking(false), 500);
            }
          },
        },
      });

      if (segments) {
        interval = setInterval(() => {
          const p = playerRef.current;
          if (!p || !p.getCurrentTime) return;
          const t = p.getCurrentTime();
          const seg = segments[segRef.current];
          if (t >= seg.end || t < seg.start - 3) {
            if (logoBetween) setMasking(true);
            const next = (segRef.current + 1) % segments.length;
            segRef.current = next;
            p.seekTo(segments[next].start, true);
          }
        }, 250);
      }
    });

    return () => {
      mounted = false;
      clearInterval(interval);
      const p = playerRef.current;
      try { if (p && typeof p.destroy === "function") p.destroy(); } catch (e) { console.debug("YT player destroy on unmount (expected)", e); }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, JSON.stringify(loops), logoBetween]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" data-testid="hero-video-loop">
      {poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <div
        ref={hostRef}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full [&>iframe]:absolute [&>iframe]:inset-0 [&>iframe]:w-full [&>iframe]:h-full"
      />
      {logoBetween && (
        <div
          className={`absolute inset-0 flex items-center justify-center bg-black transition-opacity duration-700 ${
            masking ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={club.crest}
            alt="Atlético South Florida crest"
            className={`w-44 h-44 object-contain drop-shadow-[0_4px_30px_rgba(229,184,51,0.6)] ${
              masking ? "logo-twirl" : ""
            }`}
          />
        </div>
      )}
    </div>
  );
}
